<template>
  <div>
    <v-row v-if="!$store.getters.arena">
      <v-col cols="12" class="pt-0">
        <v-alert type="info">
          <span class="mb-0 text--white">{{
            $t('adminDashboard.chooseArena')
          }}</span>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="3">
        <Generated
          :date="dateString"
          :refresh="refresh"
          :loading="
            loadingShopStats || loadingArenaStats || loadingCoreStats
          " />
      </v-col>
      <v-col cols="12" sm="6"></v-col>
      <v-col cols="12" sm="3">
        <v-btn
          v-if="isProduction"
          class="global-button"
          color="primary"
          x-large
          block
          @click="openKPIsInNewTab"
          style="text-transform: none">
          <v-icon left>mdi-finance</v-icon>KPIs
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" class="d-flex">
        <v-card class="flex-grow-1 py-4 px-6">
          <v-simple-table class="do-not-apply-hover">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="pl-0">
                    <h2>{{ $t('entities.finance.todaysIncome') }}</h2>
                  </th>
                  <th class="text-right pr-0">
                    <v-skeleton-loader
                      :loading="loadingShopStats"
                      class="d-inline-block"
                      type="image"
                      width="75px"
                      height="25px">
                      <p class="title font-weight-bold mb-0">
                        {{ todaysIncome }}
                      </p>
                    </v-skeleton-loader>
                  </th>
                </tr>
              </thead>
            </template>
          </v-simple-table>
          <v-expansion-panels :value="0" :readonly="loadingShopStats" flat>
            <v-expansion-panel>
              <v-expansion-panel-content class="grey-border">
                <v-simple-table class="do-not-apply-hover">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('miscellaneous.paymentMethod') }}
                        </th>
                        <th class="text-left">
                          {{ $t('adminDashboard.amount') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="loadingShopStats">
                        <tr>
                          <td class="pl-3" colspan="2">
                            {{
                              $t('adminDashboard.loadingIncomeByPaymentMethod')
                            }}
                            <ProgressCircular color="primary" class="mb-1" />
                          </td>
                        </tr>
                      </template>
                      <template v-else-if="payments.length">
                        <tr v-for="(pm, index) in payments" :key="index">
                          <td class="pl-3">
                            {{
                              $t(`miscellaneous.paymentMethods.${pm.i18nKey}`)
                            }}
                          </td>
                          <td class="font-weight-bold">
                            {{ pm.amount }}
                          </td>
                        </tr>
                      </template>
                      <template v-else>
                        <tr>
                          <td class="pl-3" colspan="2">
                            {{ $t('adminDashboard.noIncome') }}
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6">
        <v-row>
          <v-col cols="12" sm="6">
            <CardStat
              :loading="loadingArenaStats"
              :value="`${currentlyPlaying}`"
              :text="$t('adminDashboard.currentlyPlaying')"
              width="40px" />
          </v-col>
          <v-col cols="6" sm="3">
            <CardStat
              :loading="loadingArenaStats"
              :value="`${visitorsToday}`"
              :text="$t('adminDashboard.visitorsToday')"
              width="40px" />
          </v-col>
          <v-col cols="6" sm="3">
            <CardStat
              :loading="loadingArenaStats"
              :value="`${firstVisits}`"
              :text="$t('adminDashboard.firstVisits')"
              width="40px" />
          </v-col>
          <v-col cols="6">
            <CardStat
              :loading="loadingCoreStats"
              :value="`${newUsers}`"
              :text="$t('adminDashboard.newUsers')"
              width="40px" />
          </v-col>
          <v-col cols="6">
            <CardStat
              :loading="loadingShopStats"
              :value="`${averageSpending}`"
              :text="$t('adminDashboard.averageSpending')"
              width="80px" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6" sm="3">
        <CardStat
          :loading="loadingShopStats"
          :value="`${addThousandSeparator(soldCoins)}`"
          :text="$t('adminDashboard.soldCoins')"
          :coins="true" />
      </v-col>
      <v-col cols="6" sm="3">
        <CardStat
          :loading="loadingShopStats"
          :value="`${addThousandSeparator(freeCoins)}`"
          :text="$t('adminDashboard.freeCoins')"
          :coins="true" />
      </v-col>
      <v-col cols="6" sm="3">
        <CardStat
          :loading="loadingArenaStats"
          :value="`${Math.round(averagePlaytime / 60)} min`"
          :text="$t('adminDashboard.averagePlaytime')" />
      </v-col>
      <v-col cols="6" sm="3">
        <CardStat
          :loading="loadingCoreStats"
          :value="`${averageAge}`"
          :text="$t('adminDashboard.averageAge')"
          width="40px" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-2">
          <DataTableSkeletonLoader :loading="initialLoad" />
          <!-- Mobile -->
          <v-col
            v-if="$store.getters.arena && $vuetify.breakpoint.xs"
            cols="12"
            class="text-center pb-1">
            <RowsPerPage :pagination="pagination" />
          </v-col>
          <v-data-table
            v-show="!initialLoad"
            :headers="dailyStatsHeaders"
            :items="dailyStats"
            :dense="$vuetify.breakpoint.xs && !loadingDailyStats"
            :loading="loadingDailyStats"
            :loading-text="$t('components.noData.fetchingEntries')"
            :options.sync="options"
            :server-items-length="pagination.total"
            :items-per-page="pagination.per_page"
            :header-props="{
              sortIcon: 'arrow_circle_up',
              sortByText: $t('miscellaneous.sortBy'),
            }"
            :class="{ 'mobile-empty-datatable': $vuetify.breakpoint.xsOnly }"
            hide-default-footer>
            <template v-slot:top>
              <v-toolbar class="mb-10 pt-6" flat>
                <v-toolbar-title>{{
                  $t('adminDashboard.dailyStats')
                }}</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <small class="mr-2">{{
                  addThousandSeparator(pagination.total)
                }}</small>
                <RefreshButton
                  :refresh="fetchEntities"
                  :loading="loadingDailyStats" />
                <v-spacer />
                <!-- Desktop -->
                <v-col
                  v-if="$store.getters.arena && $vuetify.breakpoint.smAndUp"
                  cols="6"
                  sm="4"
                  md="3"
                  lg="2">
                  <RowsPerPage :pagination="pagination" />
                </v-col>
              </v-toolbar>
            </template>
            <template v-slot:item.date="{ item }">
              {{ formatDate(new Date(item.date), true, true, true) }} ({{
                formatDay(item.date).substring(0, 3)
              }})
            </template>
            <template v-slot:item.income="{ item }">
              {{ item.income }}
            </template>
            <template v-slot:item.average_spending="{ item }">
              {{ item.average_spending }}
            </template>
            <template v-slot:item.sold_coins="{ item }">
              <span
                >{{ addThousandSeparator(item.sold_coins) }} <CoinIcon
              /></span>
            </template>
            <template v-slot:item.free_coins="{ item }">
              <span
                >{{ addThousandSeparator(item.free_coins) }} <CoinIcon
              /></span>
            </template>
            <template v-slot:no-data>
              <v-row>
                <v-col class="my-4 text-center">
                  <p class="mb-0">
                    {{ $t('components.noData.noEntriesFound') }}
                  </p>
                  <v-btn
                    v-if="$store.getters.arena && !pagination.search.length"
                    @click="fetchEntities()"
                    color="primary"
                    class="mt-3"
                    outlined>
                    {{ $t('components.noData.tryAgain') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Vue from 'vue'
import CardStat from '@/components/CardStat.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import Generated from '@/components/Generated.vue'
import DashboardApi from '@/services/main/DashboardApi'
import DataTableSkeletonLoader from '@/components/crud/DataTableSkeletonLoader.vue'
import RowsPerPage from '@/components/crud/RowsPerPage.vue'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import CoinIcon from '@/components/CoinIcon.vue'

import ReportApi from '@/services/shop/ReportApi'

import thousandSeparator from '@/mixins/thousand-separator'
import { paginationWatch } from '@/mixins/crud/watch/pagination.js'
import formatDate from '@/mixins/format-date'
import formatDay from '@/mixins/format-day'
import isProduction from '@/mixins/crud/computed/isProduction'

export default Vue.extend({
  components: {
    CardStat,
    ProgressCircular,
    Generated,
    DataTableSkeletonLoader,
    RowsPerPage,
    RefreshButton,
    CoinIcon,
  },

  mixins: [
    thousandSeparator,
    paginationWatch,
    formatDate,
    formatDay,
    isProduction,
  ],

  data: () => ({
    dateString: '',
    initialLoad: true,
    loadingShopStats: false,
    loadingArenaStats: false,
    loadingCoreStats: false,
    loadingDailyStats: false,
    todaysIncome: 0,
    payments: [],
    currentlyPlaying: 0,
    visitorsToday: 0,
    firstVisits: 0,
    newUsers: 0,
    averageSpending: 0,
    soldCoins: 0,
    freeCoins: 0,
    averagePlaytime: 0,
    averageAge: 0,
    dailyStats: [],
    pagination: {
      search: '',
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: 10,
      to: 0,
      total: 0,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
  }),

  computed: {
    dailyStatsHeaders() {
      return [
        {
          text: this.$t('miscellaneous.date'),
          align: 'left',
          value: 'date',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.income'),
          align: 'left',
          value: 'income',
          sortable: false,
        },
        {
          text: this.$t('adminDashboard.averageSpending'),
          align: 'left',
          value: 'average_spending',
          sortable: false,
        },
        {
          text: this.$t('adminDashboard.soldCoins'),
          align: 'left',
          value: 'sold_coins',
          sortable: false,
        },
        {
          text: this.$t('adminDashboard.freeCoins'),
          align: 'left',
          value: 'free_coins',
          sortable: false,
        },
      ]
    },
  },

  mounted() {
    this.refresh()
  },

  methods: {
    openKPIsInNewTab() {
      const url = 'https://kpi.friendlyfireesports.com'
      window.open(url, '_blank')
    },
    async fetchArenaStats() {
      try {
        this.loadingArenaStats = true
        if (this.$store.getters.arena) {
          await DashboardApi.arenaStats(this.$store.getters.arena.id).then(
            (resp) => {
              this.currentlyPlaying = resp.data.currently_playing
              this.visitorsToday = resp.data.visitors_today
              this.firstVisits = resp.data.first_visits
              this.averagePlaytime = resp.data.average_playtime_seconds
            },
          )
        }
      } catch (error) {
        console.warn(`Arena API failed.`)
        console.log(error)
      } finally {
        this.loadingArenaStats = false
      }
    },
    async fetchCoreStats() {
      try {
        this.loadingCoreStats = true
        if (this.$store.getters.arena) {
          await DashboardApi.coreStats(this.$store.getters.arena.id).then(
            (resp) => {
              this.newUsers = resp.data.new_users
              this.averageAge = resp.data.average_age
            },
          )
        }
      } catch (error) {
        console.warn(`Core API failed.`)
        console.log(error)
      } finally {
        this.loadingCoreStats = false
      }
    },
    async fetchEntities() {
      try {
        this.loadingDailyStats = true
        if (this.$store.getters.arena) {
          await this.fetchDailyStatsShopV2()
        }
      } catch (error) {
        console.warn(`Daily Stats API failed.`)
        console.log(error)
      } finally {
        this.initialLoad = false
        this.loadingDailyStats = false
      }
    },
    async fetchDailyStatsShopV2() {
      const now = new Date()
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const dateLt = `${tomorrow.getFullYear()}-${String(
        tomorrow.getMonth() + 1,
      ).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`
      const gteDate = new Date(tomorrow)
      gteDate.setDate(gteDate.getDate() - this.pagination.per_page)
      const dateGte = `${gteDate.getFullYear()}-${String(
        gteDate.getMonth() + 1,
      ).padStart(2, '0')}-${String(gteDate.getDate()).padStart(2, '0')}`

      const response = await ReportApi.report(
        this.$store.getters.arena.id,
        dateGte,
        dateLt,
      )

      const stepReports = response.data.step_reports.filter(
        (step) => new Date(step.date_gte) <= now,
      )

      this.dailyStats = stepReports
        .map((step) => {
          const report = step.report
          const currencyCode = report.currency_code ?? ''

          return {
            date: step.date_gte.split('T')[0],
            income: `${report.gross_amount} ${currencyCode}`,
            average_spending: `${report.average_spending} ${currencyCode}`,
            sold_coins: report.coins_report.sold ?? 0,
            free_coins: report.coins_report.given ?? 0,
          }
        })
        .reverse()

      this.pagination.total = stepReports.length

      // Populate top stats from today's report (first after reverse)
      if (this.dailyStats.length) {
        const todayStep = stepReports[stepReports.length - 1]
        const todayReport = todayStep.report
        const currencyCode = todayReport.currency_code ?? ''

        this.todaysIncome = `${todayReport.gross_amount} ${currencyCode}`
        this.averageSpending = `${todayReport.average_spending} ${currencyCode}`
        this.soldCoins = todayReport.coins_report.sold ?? 0
        this.freeCoins = todayReport.coins_report.given ?? 0

        const codeToI18nKey = { CSH: 'cash', CRD: 'creditCard', BNK: 'bank' }
        this.payments = (todayReport.payment_method_incomes ?? [])
          .filter((pm) => codeToI18nKey[pm.payment_method_code])
          .map((pm) => ({
            i18nKey: codeToI18nKey[pm.payment_method_code],
            amount: `${pm.gross_amount} ${currencyCode}`,
          }))
      }
    },
    refresh() {
      this.dateString = this.formatDate(new Date(), true, true)
      this.fetchArenaStats()
      this.fetchCoreStats()
      this.fetchEntities()
    },
  },
})
</script>

<style>
.v-expansion-panel-header,
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.grey-border {
  border: 1px solid #d8d8d8;
}
</style>
